import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/public/Navbar";
import Hero from "@/components/public/Hero";
import Intro from "@/components/public/Intro";
import About from "@/components/public/About";
import Skills from "@/components/public/Skills";
import Experience from "@/components/public/Experience";
import Projects from "@/components/public/Projects";
import Clients from "@/components/public/Clients";
import Partnerships from "@/components/public/Partnerships";
import Certifications from "@/components/public/Certifications";
import Services from "@/components/public/Services";
import Testimonials from "@/components/public/Testimonials";
import FAQ from "@/components/public/FAQ";
import CTASection from "@/components/public/CTASection";
import Contact from "@/components/public/Contact";
import Footer from "@/components/public/Footer";
import FloatingWhatsApp from "@/components/public/FloatingWhatsApp";
import BackToTop from "@/components/public/BackToTop";

function HomeSkeleton() {
    return (
        <div className="min-h-screen bg-ink-900 text-white overflow-hidden animate-pulse">
            {/* Skeleton Navbar */}
            <div className="h-20 max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-white/10" />
                    <div className="space-y-1.5">
                        <div className="w-36 h-3.5 bg-white/10 rounded" />
                        <div className="w-24 h-2.5 bg-white/5 rounded" />
                    </div>
                </div>
                <div className="hidden md:flex gap-6">
                    <div className="w-12 h-3 bg-white/10 rounded" />
                    <div className="w-12 h-3 bg-white/10 rounded" />
                    <div className="w-12 h-3 bg-white/10 rounded" />
                    <div className="w-12 h-3 bg-white/10 rounded" />
                </div>
                <div className="w-28 h-9 rounded-full bg-white/10" />
            </div>

            {/* Skeleton Hero */}
            <div className="h-[80vh] max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center space-y-6">
                <div className="w-48 h-6 rounded-full bg-white/10" />
                <div className="space-y-3">
                    <div className="w-3/4 max-w-2xl h-14 bg-white/10 rounded-xl" />
                    <div className="w-1/2 max-w-lg h-14 bg-white/10 rounded-xl" />
                </div>
                <div className="w-full max-w-xl h-5 bg-white/5 rounded" />
                <div className="flex gap-4 pt-4">
                    <div className="w-44 h-12 rounded-full bg-brand/30" />
                    <div className="w-32 h-12 rounded-full bg-white/10" />
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState({
        profile: null,
        projects: [],
        skills: [],
        experiences: [],
        brands: [],
        certifications: [],
        services: [],
        testimonials: [],
        faqs: [],
        settings: null,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        // Try fast unified endpoint first (1 HTTP roundtrip)
        api.get("/home-data")
            .then((res) => {
                if (!mounted) return;
                if (res.data) {
                    setData(res.data);
                    setLoading(false);
                }
            })
            .catch(() => {
                // Graceful fallback to concurrent individual endpoints
                Promise.all([
                    api.get("/profile"),
                    api.get("/projects"),
                    api.get("/skills"),
                    api.get("/experiences"),
                    api.get("/brands"),
                    api.get("/certifications"),
                    api.get("/services"),
                    api.get("/testimonials"),
                    api.get("/faqs"),
                    api.get("/settings"),
                ])
                    .then(([p, pr, sk, ex, br, ce, sv, ts, fq, st]) => {
                        if (!mounted) return;
                        setData({
                            profile: p.data,
                            projects: pr.data,
                            skills: sk.data,
                            experiences: ex.data,
                            brands: br.data,
                            certifications: ce.data,
                            services: sv.data,
                            testimonials: ts.data,
                            faqs: fq.data,
                            settings: st.data,
                        });
                    })
                    .finally(() => mounted && setLoading(false));
            });

        return () => {
            mounted = false;
        };
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    const s = data.settings || {};

    return (
        <div data-testid="home-page" className="min-h-screen bg-ink-900 text-white overflow-x-hidden">
            <Navbar profile={data.profile} settings={data.settings} />
            <main>
                <Hero profile={data.profile} />
                <Intro profile={data.profile} />
                <About profile={data.profile} />
                {s.show_clients !== false && <Clients brands={data.brands} />}
                <Projects projects={data.projects} />
                {s.show_services !== false && <Services items={data.services} />}
                <Skills skills={data.skills} />
                {s.show_partnerships !== false && <Partnerships brands={data.brands} />}
                {s.show_testimonials !== false && <Testimonials items={data.testimonials} />}
                {s.show_certifications !== false && <Certifications items={data.certifications} />}
                <Experience experiences={data.experiences} />
                {s.show_faq !== false && <FAQ items={data.faqs} settings={data.settings} profileEmail={data.profile?.email} />}
                <CTASection profile={data.profile} settings={data.settings} />
                <Contact profile={data.profile} />
            </main>
            <Footer profile={data.profile} />
            <FloatingWhatsApp settings={data.settings} profileName={data.profile?.name?.split(" ")[0]} />
            <BackToTop />
        </div>
    );
}
